<template>
  <div
    class="form__item"
    :class="{required: required, 'default-checkbox': isCheckbox, 'radiolist': optionsRadio}"
  >
    <label v-if="label && !isCheckbox" :for="uuid" v-html="label"></label>
    <div class="form__description" v-if="description">{{description}}</div>

    <template v-if="isCheckbox">
      <label class="form__label--checkbox">
        <input
          type="checkbox"
          v-model="value"
          :required="required"
          @change="validateField"
          @blur="validateField"
          ref="input"
        />
        <span class="checkbox__label" v-html="label"></span>
      </label>
    </template>

    <template v-else-if="options.length">
      <template v-if="optionsRadio">
        <div v-for="(option, optionIndex) in options" :key="optionIndex" class="default-radio"
            :class="{ 'default-radioTs': brand === Brand.Travelsphere }">
          <input
            :id="uuid + optionIndex"
            type="radio"
            v-model="value"
            :value="optionValue(option)"
            :required="required"
            @change="validateField"
            @blur="validateField"
            ref="input"
          />
          <label :for="uuid + optionIndex" class="form__label--radio">
            <span v-html="optionLabel(option, true)"></span>
          </label>
        </div>
      </template>

      <select
        v-else
        :id="uuid"
        :name="name"
        v-model="value"
        :disabled="!enabled"
        :required="required"
        @change="validateField"
        @blur="validateField"
        :class="{'input-validation-error': showError}"
        ref="input"
      >
        <option value>{{placeholder}}</option>
        <option
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
          :value="optionValue(option)"
        >{{optionLabel(option)}}</option>
      </select>
    </template>

    <input
      v-else
      :name="name"
      :id="uuid"
      :type="type"
      :disabled="!enabled"
      :required="required"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :pattern="validationPattern"
      :title="validationTitle"
      :value="value"
      :step="step"
      :min="min"
      :max="max"
      @input="onInput"
      @change="validateField"
      @blur="validateField"
      :class="{'input-validation-error': showError}"
      ref="input"
    />

    <div v-if="isPassword" class="password-toggle" @click="togglePasswordVisibility">
      <svg v-if="fieldType === 'password'" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="eye">
          <path id="Vector" d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="#757575" stroke-linecap="round" stroke-linejoin="round"/>
          <path id="Vector_2" d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#757575" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      </svg>
      
      <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="eye-off">
          <path id="Vector" d="M9.88 9.88C9.58526 10.1547 9.34885 10.4859 9.18488 10.8539C9.02091 11.2218 8.93274 11.6191 8.92564 12.0219C8.91853 12.4247 8.99263 12.8248 9.14351 13.1984C9.2944 13.5719 9.51898 13.9113 9.80385 14.1962C10.0887 14.481 10.4281 14.7056 10.8016 14.8565C11.1752 15.0074 11.5753 15.0815 11.9781 15.0744C12.3809 15.0673 12.7782 14.9791 13.1462 14.8151C13.5142 14.6512 13.8454 14.4148 14.12 14.12" stroke="#757575" stroke-linecap="round" stroke-linejoin="round"/>
          <path id="Vector_2" d="M10.73 5.08C11.1513 5.02751 11.5754 5.00079 12 5C19 5 22 12 22 12C21.5529 12.9571 20.9922 13.8569 20.33 14.68" stroke="#757575" stroke-linecap="round" stroke-linejoin="round"/>
          <path id="Vector_3" d="M6.61 6.60999C4.62125 7.96461 3.02987 9.82524 2 12C2 12 5 19 12 19C13.9159 19.0051 15.7908 18.4451 17.39 17.39" stroke="#757575" stroke-linecap="round" stroke-linejoin="round"/>
          <path id="Vector_4" d="M2 2L22 22" stroke="#757575" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      </svg>
    </div>

    <span v-if="showError" class="field-error">{{errorMessage}}</span>
  </div>
</template>

<script>
import { v1 as uuid } from 'uuid';
import debounce from 'lodash/debounce';
import ValidationMixin from '@/vue/components/Form/validationMixin';
import {Brand} from './../../../brand.js';

export default {
  mixins: [ValidationMixin],

  props: {
    name: {
      type: [String, Number],
      default: ''
    },
    enabled: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'text'
    },
    customValidation: {
      type: Function
    },
    startingValue: {
      type: [String, Number, Boolean],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String,
      default: 'on'
    },
    description: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    optionValueKey: {
      type: String,
      default: ''
    },
    optionLabelKey: {
      type: String,
      default: ''
    },
    optionsRadio: {
      type: Boolean,
      default: false
    },
    pattern: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    step: {
      type: String,
      default: null
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    vuexPath: {
      type: Array,
      default: () => []
    },
    brand: {
      type: String,
      default: ''
    },
    trim: {
      type: Boolean,
      default: false
    },
    isPassword: {
      type: Boolean,
      default: false
    },
    fieldType: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      Brand,
      uuid: uuid()
    };
  },

  computed: {
    value: {
      get() {
        return this.$store.getters.fieldValue({
          name: this.name,
          vuexPath: this.vuexPath
        });
      },
      set(value) {
        this.$store.commit('setFieldValue', {
          name: this.name,
          value: value,
          target: this.$refs.input,
          vuexPath: this.vuexPath
        });
      }
    },

    showError() {
      return this.$store.getters.showFieldError({
        name: this.name,
        vuexPath: this.vuexPath
      });
    },

    errorMessage() {
      const errorMessage = this.$store.getters.fieldErrorMessage({
        name: this.name,
        target: this.$refs.input,
        vuexPath: this.vuexPath
      });

      if (!errorMessage) {
        switch (this.type) {
          case 'email':
            return 'Invalid email address';
          default:
            return 'This is a required field';
        }
      }

      return errorMessage;
    },

    isCheckbox() {
      return this.type === 'checkbox';
    },

    validationPattern() {
      if (this.pattern === '') {
        return null;
      }
      return this.pattern || this.patterns[this.type] || null;
    },

    validationTitle() {
      if (this.title === '') {
        return null;
      }
      return this.title || this.titles[this.type] || null;
    }
  },

  watch: {
    value() {
      this.$nextTick().then(this.setFieldError);
    }
  },

  created() {
    this.$store.commit('setupField', {
      name: this.name,
      value: this.startingValue,
      required: this.required,
      customValidation: this.customValidation,
      vuexPath: this.vuexPath
    });
  },

  beforeDestroy() {
    this.$store.commit('destroyField', {
      name: this.name,
      vuexPath: this.vuexPath
    });
  },

  mounted() {
    // set the error state initially if a startingValue is set
    if (this.startingValue) {
      this.setFieldError();
    }
  },

  methods: {
    onInput(e) {
      this.value = e.target.value;
      this.setFieldError();
    },

    setFieldError() {
      this.$store.commit('setFieldError', {
        name: this.name,
        target: this.$refs.input,
        vuexPath: this.vuexPath
      });
    },

    validateField: debounce(function() {
      if(this.trim){
        this.value = this.$refs.input.value.trim();
        this.$refs.input.value = this.value;
      }
      
      this.$store.commit('validateField', {
        name: this.name,
        target: this.$refs.input,
        vuexPath: this.vuexPath
      });

      this.$emit('change');
    }, 100),

    optionValue(option) {
      return this.optionValueKey ? option[this.optionValueKey] : option;
    },

    optionLabel(option, convertBoolean = false) {
      if (convertBoolean) {
        if (option === true) {
          return 'Yes';
        } else if (option === false) {
          return 'No';
        }
      }

      return this.optionLabelKey ? option[this.optionLabelKey] : option;
    },

    togglePasswordVisibility() {
      this.$emit('toggle-password');
    }
  }
};
</script>